import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  DefinitionType,
  BaseQueryApi,
} from '@reduxjs/toolkit/query/react';
import { RootState } from '../../redux/store';
import { apiPath, TApiPath } from 'common/apiPath/apiPath';
import { authSlice } from 'common/commonRedux/auth/slice';
import axios from 'axios';
import { setLogoutAction } from 'common/commonRedux/auth/actions';
import { BASE_URL } from 'common/config/baseUrl';
export interface ApiParams {
  params: object;
  urlObjKey: TApiPath; // Ensures urlObjKey is one of the keys in apiPath
  paramId?: string | number;
  // When true, this request uses the original (logged-in) JWT even if an
  // impersonation token is active. Use for sidebar profile, Settings APIs,
  // and the account-impersonation calls themselves.
  useOriginalToken?: boolean;
}
export interface CrudeParams extends Omit<ApiParams, 'params'> {
  params?: object;
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  payload?: object;
}

// Header marker used by callers that opt out of impersonation. Stripped in
// prepareHeaders before the request is sent.
export const USE_ORIGINAL_TOKEN_HEADER = 'x-use-original-token';
const { setLogin } = authSlice.actions;
const apiUrl = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_BASE_URL : process.env.REACT_APP_DEV_BASE_API_URL;
const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  // credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const state = (getState() as RootState).auth;
    // When impersonating, profileData.token already holds the impersonation JWT
    // and originalToken holds the stashed real-login JWT.
    const activeToken = state?.profileData?.token;
    const stashedOriginalToken = state?.originalToken;
    const useOriginal = headers.get(USE_ORIGINAL_TOKEN_HEADER) === '1';
    const tokenToUse = useOriginal ? (stashedOriginalToken || activeToken) : activeToken;
    if (tokenToUse) {
      headers.set('Authorization', `Bearer ${tokenToUse}`);
    } else {
      console.warn('No token found. Request may fail.');
    }
    if (useOriginal) headers.delete(USE_ORIGINAL_TOKEN_HEADER);
    return headers;
  },
});
const baseQueryWithRefreshToken: BaseQueryFn<
  FetchArgs,
  BaseQueryApi,
  DefinitionType
> = async (args, api, extraOptions): Promise<any> => {
  const refreshToken = (api.getState() as RootState).auth?.profileData
    ?.refreshToken;
  const encodedRefreshToken = encodeURIComponent(refreshToken);
  try {
    const result = await baseQuery(args, api, extraOptions);
    if (result?.error?.status === 401) {
      const res = await axios.get(
        `${BASE_URL}/api/Auth/RefreshToken?token=${encodedRefreshToken}`
      );
      if (res.data?.token) {
        api.dispatch(setLogin({ token: res?.data?.token }));
        return await baseQuery(args, api, extraOptions);
      } else {
        api.dispatch(setLogoutAction());
      }
    }
    return result;
  } catch (error: any) {
    api.dispatch(setLogoutAction());
    // A base query must always resolve to a `{ data } | { error }` shape —
    // returning undefined here (e.g. when the refresh-token call itself
    // throws on a network error) leaves RTK Query with a malformed result,
    // which can leave the query stuck or blow up callers that assume a
    // valid response. Surface it as a normal query error instead.
    return {
      error: {
        status: 'FETCH_ERROR',
        error: error?.message || 'Failed to fetch',
      },
    };
  }
};

// make this reusable for calling api and get data from the backend  (only for get) ----------------\
// IF there is any requirement like , have to invalid data on trigger post api , then it should be separate for every single api
// As we don't use this api calling in any post/patch/delete action so we made this reusable.

// Define a service using a base URL and expected endpoints
export const apiCalling = createApi({
  reducerPath: 'baseApi',
  baseQuery: baseQueryWithRefreshToken,
  tagTypes: ['application'],
  keepUnusedDataFor: 300, // Cache kept for 5 minutes; pages refetch by default via refetchOnMountOrArgChange: true
  refetchOnMountOrArgChange: true,
  refetchOnFocus: true, // optional: refetch when tab focus
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getData: builder.query<any, ApiParams>({
      query: ({ params, urlObjKey, paramId, useOriginalToken }) => {
        const pathValue = apiPath[urlObjKey];
        const url = typeof pathValue === 'function'
          ? pathValue(paramId as string)
          : pathValue + (paramId ? `/${paramId}` : '');
        const headers: Record<string, string> = {};
        if (useOriginalToken) headers[USE_ORIGINAL_TOKEN_HEADER] = '1';
        return { url, params, headers };
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetDataQuery, useLazyGetDataQuery } = apiCalling;
